import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { formatTimestamp } from '../../utils/timeUtils';
import LogoImage from '../../assets/images/croppedlogo.png';

// Create styles for PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottom: '1pt solid #BBDDE0',
  },
  headerLogoContainer: {
    width: 100,
  },
  headerLogo: {
    width: '100%',
  },
  headerInfoContainer: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2C3E50',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 3,
    color: '#34495E',
  },
  infoLine: {
    fontSize: 12,
    marginBottom: 3,
    color: '#7F8C8D',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 8,
    backgroundColor: '#BBDDE0',
    padding: 8,
    color: '#2C3E50',
    borderRadius: 4,
  },
  contentText: {
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 8,
    color: '#2C3E50',
    textAlign: 'justify',
    paddingLeft: 10,
    paddingRight: 10,
  },
  metadataContainer: {
    flexDirection: 'row',
    marginBottom: 8,
    paddingLeft: 10,
  },
  metadataLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    width: 150,
    color: '#2C3E50',
  },
  metadataValue: {
    fontSize: 12,
    flex: 1,
    color: '#2C3E50',
  },
  footerContainer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    borderTop: '1pt solid #BBDDE0',
    paddingTop: 10,
    fontSize: 10,
    color: '#95A5A6',
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    fontSize: 10,
    color: '#95A5A6',
  },
});

// Creates a PDF document for the clinical report
const ReportPDFDocument = ({ patientName = 'Patient Name', clinicianName = 'Clinician Name', reportData, generatedDate = new Date(), assistiveDeviceInfo }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.headerContainer}>
        <View style={styles.headerInfoContainer}>
          <Text style={styles.title}>Clinical Report</Text>
          <Text style={styles.infoLine}>Generated: {formatTimestamp(generatedDate)}</Text>
          <Text style={styles.infoLine}>Clinician: {clinicianName}</Text>
          <Text style={styles.infoLine}>Patient: {patientName || 'Patient Name'}</Text>
        </View>
        <View style={styles.headerLogoContainer}>
          <Image style={styles.headerLogo} src={LogoImage} />
        </View>
      </View>

      {/* Objective Section */}
      <View>
        <Text style={styles.sectionTitle}>Objective Statement</Text>
        <Text style={styles.contentText}>{reportData.objectiveStatement}</Text>
        <Text style={styles.contentText}>{assistiveDeviceInfo}</Text>
      </View>

      {/* Assessment Section */}
      <View>
        <Text style={styles.sectionTitle}>Assessment</Text>
        <Text style={styles.contentText}>{reportData.assessmentStatement || 'No assessment provided.'}</Text>
      </View>

      {/* AI Assessment Section (if present) */}
      {reportData.aiAssessment && (
        <View>
          <Text style={styles.sectionTitle}>Assessment of Gait</Text>
          <Text style={styles.contentText}>{reportData.aiAssessment}</Text>
        </View>
      )}

      {/* Patient Feedback Section */}
      <View>
        <Text style={styles.sectionTitle}>Patient Feedback</Text>
        <View style={styles.metadataContainer}>
          <Text style={styles.metadataLabel}>Pain Level:</Text>
          <Text style={styles.metadataValue}>{reportData.patientFeedback.painLevel}/10</Text>
        </View>
        <Text style={styles.contentText}>
          {reportData.patientFeedback.notes || 'No additional feedback provided.'}
        </Text>
      </View>

      {/* Plan Section */}
      <View>
        <Text style={styles.sectionTitle}>Plan</Text>
        <Text style={styles.contentText}>{reportData.planStatement || 'No plan specified.'}</Text>
      </View>

      {/* Other Notes (if present) */}
      {reportData.otherNotes && (
        <View>
          <Text style={styles.sectionTitle}>Additional Notes</Text>
          <Text style={styles.contentText}>{reportData.otherNotes}</Text>
        </View>
      )}

      {/* Footer */}
      <Text style={styles.footerContainer}>
        EvoWalk Clinical Report • Confidential Medical Information
      </Text>
      <Text style={styles.pageNumber}>1</Text>
    </Page>
  </Document>
);

export default ReportPDFDocument; 